import { Col, Row } from "react-bootstrap";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { hourFormOptions, minuteFormOptions } from "@/constant/Time/time";
import { useGet, usePut } from "@/apiHooks/HttpFunctions";

import BasicModal from "@/Modals/BasicModal";
import DropDown from "@/components/DropDown/DropDown";
import RenderToast from "@/components/RenderToast";
import StatusInput from "@/components/StatusInput";
import { currencyRateRefreshSchedulesApi } from "@/apis/apis";
import { getRefreshScheduleInitialValues } from "@/data/CurrencyManagement/RefreshSchedules/initial-values";
import getRefreshScheduleSchema from "@/schema/CurrencyManagement/RefreshSchedules/schema";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";

const RefreshScheduleModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${currencyRateRefreshSchedulesApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Refresh Schedule ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePut({
    apiURL: `${currencyRateRefreshSchedulesApi}/${modalState.id}`,
    onSuccess: handleSuccess("edited"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending],
    [saveEditError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getRefreshScheduleInitialValues(showData),
    validationSchema: getRefreshScheduleSchema(),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const { hour, minute, status } = values;

    const formattedHour = String(hour).padStart(2, "0");
    const formattedMinute = String(minute).padStart(2, "0");
    const time = `${formattedHour}:${formattedMinute}:00`;

    saveEdit({ time, status });
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={"Edit Refresh Schedule"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <DropDown
            mainClass="flex flex-row"
            dropDownContainer="w-[450px]"
            labelClassName="flex-grow"
            label="Hour"
            value={hourFormOptions.find(
              (item) => item.value === formik.values.hour
            )}
            setValue={(selected) =>
              formik.setFieldValue("hour", selected.value)
            }
            options={hourFormOptions}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <DropDown
            mainClass="flex flex-row"
            dropDownContainer="w-[450px]"
            labelClassName="flex-grow"
            label="Minute"
            value={minuteFormOptions.find(
              (item) => item.value === formik.values.minute
            )}
            setValue={(selected) =>
              formik.setFieldValue("minute", selected.value)
            }
            options={minuteFormOptions}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default RefreshScheduleModal;
